import React, { useState, useMemo, useEffect } from 'react';
import { Lock, TrendingUp, Truck, Users, Shield, AlertTriangle, CheckCircle2, Target, Activity, Banknote, BarChart3, Building2 } from 'lucide-react';
import useGameStore from '../store/gameStore';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../store/stableEmpties';
import { useTranslation } from '../i18n';
import { SUPPORT_ROLES } from '../data/supportRoles';
import { COMPETITORS } from '../data/competitors';
import { analyzeCompetitor, missingFamiliesForAnalysis } from '../utils/competitorAnalysis';
import { computeAskingPrice } from '../utils/acquisition';
import { computeStrategicAcquisitionValue } from '../utils/strategicAcquisitionValue';

const FAMILY_LABEL_KEYS = {
  marketing: 'comp_family_marketing',
  commerce: 'comp_family_commerce',
  admin: 'comp_family_admin',
  finance: 'comp_family_finance',
};

const RECO_TONE = {
  emerald: 'bg-emerald-950/40 text-emerald-200 border-emerald-500/40',
  amber:   'bg-amber-950/40   text-amber-200   border-amber-500/40',
  orange:  'bg-orange-950/40  text-orange-200  border-orange-500/40',
  rose:    'bg-rose-950/40    text-rose-200    border-rose-500/40',
};

export default function CompetitionAnalysis() {
  const { t } = useTranslation();
  const hiredSupport = useGameStore(s => s.hiredSupport || EMPTY_OBJECT);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const phase = useGameStore(s => s.phase || 1);
  const vehiclesCount = useGameStore(s => (s.vehicles || []).length);
  const acquiredIds = useGameStore(s => s.acquiredCompetitorIds || EMPTY_ARRAY);
  const pendingAcq = useGameStore(s => s.pendingAcquisition);
  const startAcquisition = useGameStore(s => s.startAcquisition);
  const loiRefusalCooldown = useGameStore(s => s.loiRefusalCooldown || EMPTY_OBJECT);
  // 02/06/2026 — Bug fix : the page used `(ownedDepots).length >= 3` to gate
  // acquisition CTAs, which incorrectly counted player-built depots toward
  // the 3-acquired-cap (the slice itself only blocks on acquired.length >=
  // MAX_ACQUIRED_DEPOTS). Players who had opened the 3 native slots could
  // therefore never buy a competitor. We now count ONLY acquired depots.
  const acquiredDepotsCount = acquiredIds.length;
  const MAX_ACQUIRED = 3;

  const [acquisitionTarget, setAcquisitionTarget] = useState(null);

  // 11/02/2026 (Phase 2 — Pédagogie) : marque une seule fois la visite de
  // la page Concurrence pour déclencher le concept "concurrence".
  useEffect(() => {
    if (!useGameStore.getState().competitorPageVisited) {
      useGameStore.setState({ competitorPageVisited: true });
    }
  }, []);

  const missing = useMemo(() => missingFamiliesForAnalysis(hiredSupport, SUPPORT_ROLES), [hiredSupport]);

  // ============ LOCKED STATE ============
  if (missing.length > 0) {
    return (
      <div className="max-w-3xl mx-auto py-12 px-4" data-testid="competition-analysis-locked">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
            <Lock className="w-7 h-7 text-amber-300" />
          </div>
          <h1 className="text-2xl font-display font-black text-foreground">{t('comp_locked_title')}</h1>
          <p className="text-sm text-slate-400 max-w-xl mx-auto leading-relaxed">
            {t('comp_locked_intro')}
          </p>
          <ul className="inline-flex flex-col gap-2 text-left text-sm">
            {Object.entries(FAMILY_LABEL_KEYS).map(([key, labelKey]) => {
              const has = !missing.includes(key);
              return (
                <li key={key} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${has ? 'bg-emerald-950/30 text-emerald-200 border border-emerald-500/40' : 'bg-ink-700/40 text-slate-400 border border-ink-600'}`} data-testid={`req-family-${key}`}>
                  {has ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : <Lock className="w-4 h-4 text-slate-500" />}
                  {t(labelKey)}
                </li>
              );
            })}
          </ul>
          <p className="text-xs text-slate-500 max-w-md mx-auto pt-4 italic">
            {t('comp_locked_tip')}
          </p>
        </div>
      </div>
    );
  }

  // ============ UNLOCKED — list competitors ============
  // 12/02/2026 — Passe `playerState` pour appliquer le scaling dynamique
  // (phase + flotte + jours) aux KPIs concurrent (marketScale).
  const playerState = { phase, vehicles: Array(vehiclesCount).fill(null), totalDays };
  const items = COMPETITORS.map(c => ({
    competitor: c,
    analysis: analyzeCompetitor(c, {
      hiredSupport, supportRoles: SUPPORT_ROLES, totalDays, playerState,
    }),
  }));

  return (
    <div className="max-w-6xl mx-auto py-6 px-4 space-y-6" data-testid="competition-analysis-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-black text-foreground flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-accent" />
            {t('comp_analysis_title')}
          </h1>
          <p className="text-xs text-slate-500 mt-1">{t('comp_analysis_subtitle')}</p>
        </div>
        <div className="inline-flex items-center gap-3 px-3 py-2 rounded-lg bg-ink-700/40 border border-ink-600 text-xs">
          <Building2 className="w-4 h-4 text-slate-400" />
          <span className="text-slate-300">
            {t('comp_owned_depots')} <strong className="text-foreground font-mono">{acquiredDepotsCount}/{MAX_ACQUIRED}</strong>
          </span>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="competitor-cards-grid">
        {items.map(({ competitor, analysis }) => {
          const acquired = acquiredIds.includes(competitor.id);
          const cooldownDay = loiRefusalCooldown[competitor.id];
          const cooldownLeft = cooldownDay ? Math.max(0, 90 - (totalDays - cooldownDay)) : 0;
          return (
            <CompetitorCard
              key={competitor.id}
              competitor={competitor}
              analysis={analysis}
              acquired={acquired}
              cooldownLeft={cooldownLeft}
              pendingAcq={pendingAcq}
              maxDepotsReached={acquiredDepotsCount >= MAX_ACQUIRED}
              t={t}
              onStartAcquisition={() => {
                const result = startAcquisition(competitor.id);
                if (result.ok) setAcquisitionTarget(competitor.id);
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function CompetitorCard({ competitor, analysis, acquired, cooldownLeft, pendingAcq, maxDepotsReached, t, onStartAcquisition }) {
  const asking = computeAskingPrice(analysis);
  const reco = analysis.recommendation || { tier: 'fair', label: t('comp_reco_fair'), tone: 'amber' };
  const tone = RECO_TONE[reco.tone] || RECO_TONE.amber;
  // 02/06/2026 — Strategic acquisition value KPI. We re-read totalDays
  // from the live store to avoid prop-drilling ; the value is cheap to
  // compute and recomputes naturally on every render the card has.
  const totalDays = useGameStore(s => s.totalDays || 0);
  const yearsPlayed = Math.floor(totalDays / 360);
  const strategic = computeStrategicAcquisitionValue(competitor, analysis, { yearsPlayed });

  const canAcquire = !acquired && !pendingAcq && cooldownLeft === 0 && !maxDepotsReached;
  let lockedReason = null;
  if (acquired) lockedReason = t('comp_already_acquired_reason');
  else if (pendingAcq) lockedReason = t('comp_ma_in_progress');
  else if (cooldownLeft > 0) lockedReason = t('comp_loi_cooldown', { days: cooldownLeft });
  else if (maxDepotsReached) lockedReason = t('comp_depot_quota_reached');

  return (
    <div
      className="bg-ink-800 rounded-xl border border-ink-600 overflow-hidden hover:border-ink-500 transition-colors"
      data-testid={`competitor-card-${competitor.id}`}
    >
      {/* Header */}
      <div
        className="px-4 py-3 flex items-center gap-3 border-b border-ink-700"
        style={{ background: `linear-gradient(135deg, ${competitor.color}22, ${competitor.color}06)` }}
      >
        {competitor.logo ? (
          <img
            src={competitor.logo}
            alt={competitor.name}
            className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border-2"
            style={{ borderColor: competitor.color }}
          />
        ) : (
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
            style={{ background: competitor.color }}
          >
            {competitor.avatar}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-bold text-foreground truncate">{competitor.name}</h2>
          <p className="text-[10px] text-slate-500 italic">{competitor.slogan}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${tone}`} data-testid={`reco-${competitor.id}`}>
          {reco.label}
        </span>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Key KPIs */}
        <div className="grid grid-cols-4 gap-2 text-center">
          <Kpi icon={TrendingUp}        label={t('comp_kpi_ca')}      value={`${(analysis.ca || 0).toLocaleString('fr-FR')} €`} testId={`kpi-ca-${competitor.id}`} />
          <Kpi icon={Truck}             label={t('comp_kpi_fleet')}         value={analysis.fleet || 0} testId={`kpi-fleet-${competitor.id}`} />
          <Kpi icon={Users}             label={t('comp_kpi_drivers')}     value={analysis.drivers || 0} testId={`kpi-drivers-${competitor.id}`} />
          <Kpi icon={Activity}          label={t('comp_kpi_reputation')}     value={`${analysis.reputation || 0}/100`} testId={`kpi-reputation-${competitor.id}`} />
        </div>

        {/* Financials */}
        <div className="grid grid-cols-3 gap-2 text-xs">
          <Stat label={t('comp_stat_equity')} value={`${(analysis.equity || 0).toLocaleString('fr-FR')} €`} positive={(analysis.equity || 0) > 0} />
          <Stat label={t('comp_stat_net_margin')}      value={`${Math.round((analysis.netMargin || 0) * 100)} %`} positive={(analysis.netMargin || 0) >= 0.06} />
          <Stat label={t('comp_stat_net_debt')}      value={`${(analysis.netDebt || 0).toLocaleString('fr-FR')} €`} positive={(analysis.netDebt || 0) < (analysis.ca || 1) * 15} />
        </div>

        {/* Health bar */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500">
            <span>{t('comp_health_score')}</span>
            <span className="text-foreground font-mono font-bold">{analysis.healthScore}/100</span>
          </div>
          <div className="h-1.5 bg-ink-700 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                analysis.healthScore >= 65 ? 'bg-emerald-400'
                : analysis.healthScore >= 45 ? 'bg-amber-400'
                : analysis.healthScore >= 25 ? 'bg-orange-400'
                : 'bg-rose-400'
              }`}
              style={{ width: `${analysis.healthScore}%` }}
            />
          </div>
        </div>

        {/* 02/06/2026 — Strategic acquisition value (composite 0-100) */}
        {strategic && (
          <StrategicValueCard strategic={strategic} t={t} competitorId={competitor.id} />
        )}

        {/* Weakness */}
        {analysis.weakness && analysis.weakness.tag !== 'none' && (
          <div className="flex items-start gap-2 p-2 rounded bg-amber-950/30 border border-amber-500/30 text-xs">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-300 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">{t('comp_exploitable_weakness')}</div>
              <p className="text-amber-100">{analysis.weakness.label}</p>
            </div>
          </div>
        )}

        {/* DAF asking price */}
        <div className="flex items-center justify-between p-2 rounded bg-blue-950/30 border border-blue-500/30">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-blue-300 font-bold">{t('comp_asking_price_label')}</div>
            <p className="text-lg font-mono font-bold text-blue-100" data-testid={`asking-price-${competitor.id}`}>
              {asking.toLocaleString('fr-FR')} €
            </p>
          </div>
          <Target className="w-6 h-6 text-blue-400" />
        </div>

        {/* CTA */}
        {acquired ? (
          <div className="flex items-center justify-center gap-2 py-2 rounded-lg bg-emerald-950/40 border border-emerald-500/40 text-emerald-200 text-xs font-bold uppercase tracking-wider" data-testid={`already-acquired-${competitor.id}`}>
            <Shield className="w-4 h-4" />
            {t('comp_already_acquired_badge')}
          </div>
        ) : canAcquire ? (
          <button
            type="button"
            onClick={onStartAcquisition}
            className="w-full py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-sm font-bold flex items-center justify-center gap-2 transition-all"
            data-testid={`start-acquisition-${competitor.id}`}
          >
            <Banknote className="w-4 h-4" />
            {t('comp_initiate_acquisition')}
          </button>
        ) : (
          <div className="w-full py-2 rounded-lg bg-ink-700/40 border border-ink-600 text-slate-400 text-xs text-center" data-testid={`locked-${competitor.id}`}>
            {lockedReason}
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testId }) {
  return (
    <div className="space-y-1" data-testid={testId}>
      <Icon className="w-4 h-4 text-slate-400 mx-auto" />
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-bold text-foreground font-mono">{value}</div>
    </div>
  );
}

function Stat({ label, value, positive }) {
  return (
    <div className={`p-2 rounded border ${positive ? 'bg-emerald-950/20 border-emerald-500/25' : 'bg-rose-950/20 border-rose-500/25'}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xs font-mono font-bold ${positive ? 'text-emerald-200' : 'text-rose-200'}`}>{value}</div>
    </div>
  );
}

const STRATEGIC_TONE = {
  emerald: {
    border: 'border-emerald-500/40', bg: 'bg-emerald-950/30',
    text: 'text-emerald-200', accent: 'text-emerald-300', bar: 'bg-emerald-400',
  },
  sky: {
    border: 'border-sky-500/40', bg: 'bg-sky-950/30',
    text: 'text-sky-200', accent: 'text-sky-300', bar: 'bg-sky-400',
  },
  amber: {
    border: 'border-amber-500/40', bg: 'bg-amber-950/30',
    text: 'text-amber-200', accent: 'text-amber-300', bar: 'bg-amber-400',
  },
  rose: {
    border: 'border-rose-500/40', bg: 'bg-rose-950/30',
    text: 'text-rose-200', accent: 'text-rose-300', bar: 'bg-rose-400',
  },
};

/**
 * 02/06/2026 — Strategic acquisition value card. Composite 0-100 score
 * with per-dimension breakdown to teach the apprenant *why* a competitor
 * is rated as such (fleet × specialties × revenue × reach).
 */
function StrategicValueCard({ strategic, t, competitorId }) {
  const styles = STRATEGIC_TONE[strategic.tone] || STRATEGIC_TONE.amber;
  return (
    <div
      className={`rounded-lg border ${styles.border} ${styles.bg} p-2.5 space-y-2`}
      data-testid={`strategic-value-${competitorId}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Target className={`w-3.5 h-3.5 ${styles.accent}`} />
          <span className={`text-[10px] uppercase tracking-wider font-bold ${styles.accent}`}>
            {t('comp_strategic_value_label')}
          </span>
        </div>
        <div className="text-right">
          <span className={`text-base font-display font-black ${styles.text}`} data-testid={`strategic-score-${competitorId}`}>
            {strategic.score}
          </span>
          <span className="text-[10px] text-slate-500 font-mono">/100</span>
        </div>
      </div>
      <p className={`text-[11px] font-semibold ${styles.text}`}>{t(`comp_strategic_tier_${strategic.tier}`)}</p>
      {/* Breakdown — 4 mini-bars */}
      <div className="space-y-1 pt-1 border-t border-white/5">
        <BreakdownBar label={t('comp_strategic_dim_fleet')} score={strategic.breakdown.fleet} bar={styles.bar} testid={`strategic-fleet-${competitorId}`} />
        <BreakdownBar label={t('comp_strategic_dim_specialties')} score={strategic.breakdown.specialties} bar={styles.bar} testid={`strategic-specialties-${competitorId}`} />
        <BreakdownBar label={t('comp_strategic_dim_revenue')} score={strategic.breakdown.revenue} bar={styles.bar} testid={`strategic-revenue-${competitorId}`} />
        <BreakdownBar label={t('comp_strategic_dim_reach')} score={strategic.breakdown.reach} bar={styles.bar} testid={`strategic-reach-${competitorId}`} />
      </div>
    </div>
  );
}

function BreakdownBar({ label, score, bar, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <span className="text-[10px] text-slate-400 w-24 shrink-0">{label}</span>
      <div className="flex-1 h-1 bg-ink-700 rounded-full overflow-hidden">
        <div className={`h-full transition-all ${bar}`} style={{ width: `${Math.max(2, Math.min(100, score))}%` }} />
      </div>
      <span className="text-[10px] text-slate-300 font-mono w-7 text-right">{score}</span>
    </div>
  );
}
